import { generate3DModelFilename } from '@/utils/file-utils';
import { Message, StlTier } from '@shared/types';
import { supabase } from '@/lib/supabase';

// Bucket the implicit microservice uploads offloaded tiers to (matches the
// service's STL_TIER_BUCKET default — see implicit-microservice/Supabase).
const STL_TIER_BUCKET = 'stl-tiers';

// On-demand DXF generator. The OpenSCAD worker produces DXF output by recompiling
// the source through a top-down projection, so consumers receive a callback rather
// than a ready blob.
export type DxfExporter = () => Promise<Blob>;

interface DownloadOptions {
  content: Blob | string;
  filename: string;
  mimeType?: string;
}

interface GenerateDownloadFilenameOptions {
  currentMessage?: Message | null;
  fallback?: string;
  extension: string;
}

/**
 * Downloads a file by creating a temporary download link
 */
export function downloadFile({
  content,
  filename,
  mimeType = 'application/octet-stream',
}: DownloadOptions): void {
  let blob: Blob;

  if (typeof content === 'string') {
    blob = new Blob([content], { type: mimeType });
  } else {
    blob = content;
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename for downloads using the 3D model filename utility
 */
export function generateDownloadFilename({
  currentMessage,
  fallback = 'parametric-model',
  extension,
}: GenerateDownloadFilenameOptions): string {
  const baseName = generate3DModelFilename({
    conversationTitle: undefined,
    assistantMessage: currentMessage || undefined,
    modelName: undefined,
    fallback,
  });

  return `${baseName}.${extension}`;
}

/**
 * Downloads STL file from blob
 */
export function downloadSTLFile(
  output: Blob,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'stl',
  });

  downloadFile({
    content: output,
    filename,
    mimeType: 'application/octet-stream',
  });
}

// gzip magic bytes — tier payloads (inline and stored) are gzipped.
function isGzip(bytes: Uint8Array): boolean {
  return bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
}

async function inflateIfGzip(bytes: Uint8Array): Promise<Uint8Array> {
  if (!isGzip(bytes)) return bytes;
  const stream = new Blob([bytes])
    .stream()
    .pipeThrough(new DecompressionStream('gzip'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Downloads one STL fidelity tier. Tiers are either inline (small, gzip+base64)
 * or stored (signed URL). Both are gunzipped if still compressed on arrival
 * (signed URLs are served with Content-Encoding: gzip, which browsers usually
 * inflate transparently — the magic-byte check covers the case they don't).
 */
export async function downloadStlTier(
  tier: StlTier,
  currentMessage?: Message | null,
): Promise<void> {
  let bytes: Uint8Array;
  if (tier.stl_b64) {
    const bin = atob(tier.stl_b64);
    bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  } else if (tier.path) {
    // Re-sign on demand — the baked `signedUrl` is minted at generation time
    // and expires (Supabase signed URLs are time-limited), so an old part's
    // download would otherwise 404. The bucket-relative `path` never rots (#45).
    const { data, error } = await supabase.storage
      .from(STL_TIER_BUCKET)
      .createSignedUrl(tier.path, 3600);
    if (error || !data?.signedUrl) {
      throw new Error('Could not sign download URL.');
    }
    const res = await fetch(data.signedUrl);
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
    bytes = new Uint8Array(await res.arrayBuffer());
  } else if (tier.signedUrl) {
    const res = await fetch(tier.signedUrl);
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
    bytes = new Uint8Array(await res.arrayBuffer());
  } else {
    throw new Error('This download option has no payload.');
  }

  bytes = await inflateIfGzip(bytes);
  // GLB tiers download as .glb; STL (and legacy untagged) tiers as .stl.
  const extension = tier.format === 'glb' ? 'glb' : 'stl';
  const filename = generateDownloadFilename({
    currentMessage,
    extension,
  });
  downloadFile({
    content: new Blob([bytes], { type: 'application/octet-stream' }),
    filename,
  });
}

/**
 * Downloads OpenSCAD code as .scad file
 */
export function downloadOpenSCADFile(
  code: string,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'scad',
  });

  downloadFile({
    content: code,
    filename,
    mimeType: 'text/plain',
  });
}

/**
 * Downloads build123d Python code as .py file
 */
export function downloadPythonFile(
  code: string,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'py',
  });
  downloadFile({ content: code, filename, mimeType: 'text/plain' });
}

/**
 * Downloads STEP file from base64 string
 */
export function downloadSTEPFile(
  stepB64: string,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'step',
  });
  const bytes = Uint8Array.from(atob(stepB64), (c) => c.charCodeAt(0));
  downloadFile({
    content: new Blob([bytes], { type: 'application/octet-stream' }),
    filename,
  });
}

/**
 * Downloads a parametric model's STEP (B-rep) export. Prefers the inline
 * `step_b64` blob; otherwise re-signs the bucket-relative `step_path` on demand
 * (mirroring downloadStlTier — the baked signed URL expires) and streams it,
 * gunzipping if the bytes arrive compressed.
 */
export async function downloadSTEPFromArtifact(
  artifact: { step_b64?: string; step_path?: string },
  currentMessage?: Message | null,
): Promise<void> {
  if (artifact.step_b64) {
    downloadSTEPFile(artifact.step_b64, currentMessage);
    return;
  }
  if (artifact.step_path) {
    const { data, error } = await supabase.storage
      .from(STL_TIER_BUCKET)
      .createSignedUrl(artifact.step_path, 3600);
    if (error || !data?.signedUrl) {
      throw new Error('Could not sign download URL.');
    }
    const res = await fetch(data.signedUrl);
    if (!res.ok) throw new Error(`Download failed (HTTP ${res.status})`);
    const bytes = await inflateIfGzip(new Uint8Array(await res.arrayBuffer()));
    downloadFile({
      content: new Blob([bytes], { type: 'application/octet-stream' }),
      filename: generateDownloadFilename({ currentMessage, extension: 'step' }),
    });
    return;
  }
  throw new Error('This model has no STEP payload.');
}

/**
 * Downloads DXF file from blob
 */
export function downloadDXFFile(
  output: Blob,
  currentMessage?: Message | null,
): void {
  const filename = generateDownloadFilename({
    currentMessage,
    extension: 'dxf',
  });

  downloadFile({
    content: output,
    filename,
    mimeType: 'application/dxf',
  });
}
